import React from 'react';
import { CONTACT } from '@/utils/format';
import { Link } from 'react-router-dom';
import { Award, Leaf, Users, ShieldCheck, Mail, Phone, MapPin } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

export const About = () => (
  <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
    <div className="mb-12">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Sobre nosotros</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight mb-4">
        Nutrición premium con propósito
      </h1>
      <p className="text-lg text-[#57534E] leading-relaxed max-w-3xl">
        En Nutripetgourmet creemos que cada mascota merece una alimentación adecuada a su edad, tamaño y necesidades específicas. Por eso trabajamos únicamente con marcas premium y súper premium reconocidas mundialmente.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
      {[
        { icon: Award, title: 'Marcas premium', desc: 'Trabajamos solo con las mejores marcas del mercado' },
        { icon: Leaf, title: 'Ingredientes naturales', desc: 'Productos con ingredientes reales y de calidad' },
        { icon: Users, title: 'Asesoría experta', desc: 'Te ayudamos a elegir el mejor alimento' },
        { icon: ShieldCheck, title: 'Compra segura', desc: 'Pagos protegidos y envíos confiables' }
      ].map((b, i) => (
        <div key={i} className="bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#2A5C43]/10 flex items-center justify-center mx-auto mb-3">
            <b.icon className="w-6 h-6 text-[#2A5C43]" />
          </div>
          <h3 className="font-display font-semibold text-[#1C1917] mb-2">{b.title}</h3>
          <p className="text-sm text-[#57534E]">{b.desc}</p>
        </div>
      ))}
    </div>

    <div className="bg-[#2A5C43] rounded-3xl p-10 text-white text-center">
      <h2 className="font-display font-bold text-3xl tracking-tight mb-3">¿Listo para encontrar el alimento perfecto?</h2>
      <p className="text-white/80 mb-6">Responde nuestro test personalizado</p>
      <Button asChild className="bg-[#D47A4A] hover:bg-[#B85F35] rounded-full px-8">
        <Link to="/encuentra-alimento">Empezar ahora</Link>
      </Button>
    </div>
  </div>
);

const faqs = [
  {
    q: '¿Realizan envíos a toda Colombia?',
    a: 'Sí, hacemos envíos a todos los municipios de Colombia. El costo y tiempo de entrega dependen del destino, peso del pedido y transportadora. En Bogotá el tiempo estimado es de 1-2 días hábiles, en otras ciudades principales de 3-5 días hábiles.'
  },
  {
    q: '¿Los precios incluyen IVA?',
    a: 'Sí, todos los precios que ves en el sitio incluyen IVA. No hay costos ocultos, solo se suma el envío calculado según tu ubicación al finalizar la compra.'
  },
  {
    q: '¿Qué métodos de pago aceptan?',
    a: 'Aceptamos pagos con Bold, la plataforma más segura de Colombia. Puedes pagar con tarjeta de crédito, débito, PSE o Nequi. También aceptamos transferencias bancarias contactándonos por WhatsApp.'
  },
  {
    q: '¿Puedo cambiar el alimento de mi mascota?',
    a: 'Sí, pero recomendamos hacer una transición gradual durante 7-10 días. Mezcla el alimento nuevo con el antiguo, aumentando poco a poco la proporción del nuevo. Para nutrición especializada, consulta primero con tu veterinario.'
  },
  {
    q: '¿Cómo sé qué alimento es el adecuado para mi mascota?',
    a: 'Usa nuestro cuestionario "Encuentra el alimento ideal" o contáctanos por WhatsApp. Nuestro equipo te ayudará a elegir según la edad, tamaño, actividad y necesidades particulares de tu mascota.'
  },
  {
    q: '¿Aceptan devoluciones?',
    a: 'Aceptamos devoluciones dentro de los 5 días hábiles siguientes a la entrega, siempre que el producto esté sellado. Consulta nuestra política completa de cambios y devoluciones.'
  },
  {
    q: '¿Ofrecen envío gratis?',
    a: 'Ofrecemos envío gratis a partir de un valor mínimo de compra que varía según la ciudad. Este beneficio se activa automáticamente al finalizar tu compra.'
  },
  {
    q: '¿Los productos son originales?',
    a: 'Sí, todos nuestros productos son 100% originales. Trabajamos directamente con los distribuidores oficiales de cada marca en Colombia.'
  }
];

export const FAQ = () => (
  <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
    <div className="mb-10 text-center">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Ayuda</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight">
        Preguntas frecuentes
      </h1>
    </div>

    <Accordion type="single" collapsible className="space-y-3">
      {faqs.map((faq, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-[#E7E5E4] px-6">
          <AccordionTrigger className="font-display font-semibold text-[#1C1917] text-left" data-testid={`faq-${i}`}>
            {faq.q}
          </AccordionTrigger>
          <AccordionContent className="text-[#57534E] leading-relaxed">
            {faq.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>

    <div className="text-center mt-10">
      <p className="text-[#57534E] mb-4">¿No encontraste lo que buscas?</p>
      <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
        <Link to="/contacto">Contáctanos</Link>
      </Button>
    </div>
  </div>
);

export const Contact = () => (
  <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
    <div className="mb-10">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Contacto</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight mb-3">
        Estamos para ayudarte
      </h1>
      <p className="text-[#57534E]">Contáctanos por cualquiera de nuestros canales</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-[#2A5C43]/10 flex items-center justify-center mx-auto mb-3">
          <MapPin className="w-6 h-6 text-[#2A5C43]" />
        </div>
        <h3 className="font-display font-semibold text-[#1C1917] mb-2">Dirección</h3>
        <p className="text-sm text-[#57534E]">Bogotá, Colombia</p>
      </div>
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-[#2A5C43]/10 flex items-center justify-center mx-auto mb-3">
          <Phone className="w-6 h-6 text-[#2A5C43]" />
        </div>
        <h3 className="font-display font-semibold text-[#1C1917] mb-2">Teléfono</h3>
        <p className="text-sm text-[#57534E]">{CONTACT.phoneDisplay}</p>
      </div>
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-[#2A5C43]/10 flex items-center justify-center mx-auto mb-3">
          <Mail className="w-6 h-6 text-[#2A5C43]" />
        </div>
        <h3 className="font-display font-semibold text-[#1C1917] mb-2">Correo</h3>
        <p className="text-sm text-[#57534E]">{CONTACT.email}</p>
      </div>
    </div>
  </div>
);

export const OffersPage = () => (
  <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
    <div className="mb-10">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Ofertas</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight">
        Ofertas y promociones
      </h1>
    </div>
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-16 text-center">
      <p className="text-lg text-[#57534E]">Próximamente nuevas ofertas y descuentos exclusivos</p>
      <Button asChild className="mt-6 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
        <Link to="/perros">Ver catálogo</Link>
      </Button>
    </div>
  </div>
);

export const SearchResults = () => {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const searchParams = new URLSearchParams(window.location.search);
  const query = searchParams.get('q');

  React.useEffect(() => {
    if (query) {
      import('@/utils/api').then(({ productsAPI }) => {
        productsAPI.search(query).then((r) => {
          setProducts(r);
          setLoading(false);
        });
      });
    }
  }, [query]);

  const ProductCard = React.lazy(() => import('@/components/product/ProductCard'));

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
      <div className="mb-8">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Búsqueda</p>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">
          Resultados para "{query}"
        </h1>
        <p className="text-[#57534E] mt-2">{products.length} productos encontrados</p>
      </div>
      <React.Suspense fallback={<div>Cargando...</div>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </React.Suspense>
    </div>
  );
};
